import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';

interface SiteCustomizationData {
  identity: { siteName?: string; logoUrl?: string; faviconUrl?: string; slogan?: string };
  colors: { primary?: string; secondary?: string };
  home: {
    hero?: {
      title?: string;
      subtitle?: string;
      ctaText?: string;
      ctaLink?: string;
      imageUrl?: string;
    };
    featuredServices?: Array<{ 
      id: string;
      title: string;
      description: string;
      imageUrl: string;
      link?: string;
    }>;
    testimonials?: Array<{ 
      id: string;
      name: string;
      role: string;
      quote: string;
      rating: number;
    }>;
  };
  about: { title?: string; content?: string; imageUrl?: string };
  contact: { email?: string; phone?: string; address?: string };
  seo: { metaTitle?: string; metaDescription?: string; ogImageUrl?: string };
  integrations?: {
    stripePublishableKey?: string;
  };
}

interface SiteCustomizationContextType {
  allConfig: SiteCustomizationData | null;
  loading: boolean;
  error: string | null;
  fetchAllConfig: () => Promise<void>;
  updateConfig: (data: Partial<SiteCustomizationData>) => Promise<void>;
  deleteEntity: (path: string, identifier: { id?: string; index?: number; publicId?: string }) => Promise<void>;
  reload: () => Promise<void>;
}

const SiteCustomizationContext = createContext<SiteCustomizationContextType | undefined>(undefined);

let fetchInitiated = false; // Module-level guard

export const SiteCustomizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [allConfig, setAllConfig] = useState<SiteCustomizationData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPublicConfig = useCallback(async (force = false) => {
    if (!force && fetchInitiated) {
      return;
    }
    fetchInitiated = true;
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.siteCustomization.getPublic();
      if (response.data.success) {
        setAllConfig(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch public config');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load site configuration.');
     // toast.error(err.message || 'Erreur lors du chargement de la configuration du site.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.siteCustomization.getAll();
      if (response.data.success) {
        setAllConfig(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch all config');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load all site configuration.');
      //toast.error(err.message || 'Erreur lors du chargement de la configuration complète.');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateConfig = useCallback(async (data: Partial<SiteCustomizationData>) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.siteCustomization.update(data);
      if (response.data.success) {
        toast.success('Configuration mise à jour avec succès !');
        await reload(); // Reload after successful update
      } else {
        throw new Error(response.data.message || 'Failed to update config');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update site configuration.');
     // toast.error(err.message || 'Erreur lors de la mise à jour de la configuration.');
      throw err; // Re-throw to allow component to handle
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteEntity = useCallback(async (path: string, identifier: { id?: string; index?: number; publicId?: string }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.siteCustomization.deleteSubEntity(path, identifier);
      if (response.data.success) {
       // toast.success('Élément supprimé avec succès !');
        await reload(); // Reload after successful deletion
      } else {
        throw new Error(response.data.message || 'Failed to delete entity');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete entity.');
    //  toast.error(err.message || 'Erreur lors de la suppression de l\'élément.');
      throw err; // Re-throw to allow component to handle
    } finally {
      setLoading(false);
    }
  }, []);

  const reload = useCallback(async () => {
    await fetchPublicConfig(true); // Force re-fetch of public config
  }, [fetchPublicConfig]);

  useEffect(() => {
    // Initial fetch for public config on mount
    fetchPublicConfig();
  }, [fetchPublicConfig]);

  const value = useMemo(() => ({
    allConfig,
    loading,
    error,
    fetchAllConfig,
    updateConfig,
    deleteEntity,
    reload,
  }), [allConfig, loading, error, fetchAllConfig, updateConfig, deleteEntity, 
    reload
  ]);

  return (
    <SiteCustomizationContext.Provider value={value}>
      {children}
    </SiteCustomizationContext.Provider>
  );
};

export const useSiteConfig = () => {
  const context = useContext(SiteCustomizationContext);
  if (context === undefined) {
    throw new Error('useSiteConfig must be used within a SiteCustomizationProvider');
  }
  return context;
};
